<script setup lang="ts">
import { shallowRef, useSlots } from 'vue'
import { useScroll } from '@vueuse/core'
import { QBtn } from 'quasar'
import { Color } from '@previo/codebooks'
import PCheckbox from '../../fields/PCheckbox.vue'
import PIcon from '../../icon/PIcon.vue'
import PSpace from '../../space/PSpace.vue'
import { PSpaceSize } from '../../space/types'

const selected = defineModel<boolean | null>('selected', { default: false })
const expanded = defineModel<boolean>('expanded', { default: false })

type Props = {
  selectable?: boolean
  selectDisabled?: boolean
  expandable?: boolean
  hideExpandButton?: boolean
}

withDefaults(defineProps<Props>(), {
  selectable: false,
  selectDisabled: false,
  expandable: false,
  hideExpandButton: false,
})

type Emits = {
  (e: 'scroll', xPos: number): void
  (e: 'rowClick', evt: Event): void
}

const emits = defineEmits<Emits>()

const slots = useSlots()

const scrollArea = shallowRef<HTMLElement | null>(null)
const { x, measure } = useScroll(scrollArea, { behavior: 'auto' })

const setScrollHorizontalPosition = (value: number): void => {
  x.value = value
}

const onScroll = () => {
  measure()
  emits('scroll', x.value)
}

const onRowClick = (e: Event) => {
  emits('rowClick', e)
}

const toggleExpanded = (): void => {
  expanded.value = !expanded.value
}

defineExpose({
  setScrollHorizontalPosition,
})
</script>

<template>
  <div class="p-flex p-flex-flow-column ds-data-table-item-mobile">
    <div class="p-flex p-items-center p-no-wrap">
      <div v-if="expandable" class="ds-data-table-item-mobile-expand-col">
        <q-btn
          v-if="!hideExpandButton"
          flat
          dense
          padding="0"
          data-cy="expand"
          @click="toggleExpanded"
        >
          <p-icon :icon="expanded ? 'minus' : 'plus'" :color="Color.GREY_600" />
        </q-btn>
      </div>

      <p-checkbox v-if="selectable" v-model="selected" :disable="selectDisabled" cy="selected" />

      <slot name="title" />
      <p-space :mobile="PSpaceSize.GROW" />

      <template v-if="!!slots['actions']">
        <p-space :mobile="PSpaceSize.XS" />
        <slot name="actions" />
      </template>
    </div>

    <p-space :mobile="PSpaceSize.XS" vertical />

    <div
      ref="scrollArea"
      class="p-flex p-items-center p-no-wrap p-text-no-wrap overflow-auto ds-data-table-item-hide-scrollbar"
      @scroll="onScroll"
      @click="onRowClick"
    >
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../assets/scss/shared/ds.variables.module';

.ds-data-table-item-mobile-expand-col {
  padding-left: $space-xs-mobile;
  padding-right: $space-xs-mobile;
  flex-shrink: 0;
  min-width: 28px;
}

.ds-data-table-item-hide-scrollbar {
  margin-left: $space-xs-mobile;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ds-data-table-item-hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
